import { useRef, useState } from 'react';
import { ActionButton, ActionLink } from '../../../parts/index.js';
import { ApiError } from '../../../api.js';
import type { Action, Added } from '../contract.js';
import { addApi } from '../add/api.js';
import { InvitationResult, time } from '../add/InvitationResult.js';
import { NotSaved, Refused } from '../states/States.js';
import { Change } from '../sources/Change.js';
import { park, leave, post, invite, type HomeWriteResult } from './api.js';
import { actionAddress, navigate } from './addresses.js';

export function ActionControl({ action, store, itemKey, lead = false, onChanged, onExpanded, asLink = false }: {
  asLink?: boolean; action: Action; store: string; itemKey?: string | undefined; lead?: boolean;
  // stale: the server answered 409 (the item changed meanwhile); the page reloads instead of hiding it (brief 6.8).
  onChanged?: ((words?: string, stale?: boolean, receipt?: HomeWriteResult) => void) | undefined;
  onExpanded?: ((expanded: boolean) => void) | undefined;
}) {
  const label = action.kind === 'park' ? 'Park until...' : action.kind === 'leave' ? 'Leave it' : action.label;
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const retry = useRef<(() => Promise<void>) | null>(null);
  const posted = useRef<HomeWriteResult>(undefined);
  const [invitation, setInvitation] = useState<Added | null>(null);
  const invitationSaved = useRef<{ result: Added; reason: string } | null>(null);
  // The pause went away meanwhile (404: the switch was turned off): close and reload, never draw "not found" (round 11).
  const gone = useRef(false);
  const inflight = useRef(false);

  async function sendInvitation(reason: string) {
    if (action.kind !== 'post' || !action.invite) return;
    const result = await invite(store, action.path, reason);
    invitationSaved.current = { result, reason };
    setInvitation(result);
  }
  function invitationChanged() {
    const result = invitationSaved.current?.result;
    if (action.kind === 'post' && action.invite && result?.invited) {
      // Home keeps this sentence in its saved card when the refreshed Waiting row disappears.
      onChanged?.(`${action.invite.name} is in ${action.invite.group}. The invitation went at ${time(result.sentAt)}.`, false, undefined);
    }
  }
  async function run(work: () => Promise<void>) {
    if (inflight.current) return;
    inflight.current = true; retry.current = work; setBusy(true); setError(null);
    try { await work(); setExpanded(false); onExpanded?.(false); } catch (e) {
      if (e instanceof ApiError && e.status === 409) { setExpanded(false); onExpanded?.(false); onChanged?.(e.message, true); }
      else setError(e);
    }
    finally { inflight.current = false; setBusy(false); }
  }
  function press() {
    if (action.kind === 'open') { navigate(actionAddress(action)); return; }
    if (action.kind === 'park' || action.kind === 'post') { setExpanded(true); onExpanded?.(true); return; }
    if (action.kind === 'copy') {
      void run(async () => { await navigator.clipboard.writeText(action.text); setCopied(true); }); return;
    }
    if (action.kind === 'leave' && itemKey) void run(async () => { await leave(store, itemKey); onChanged?.(); });
  }
  if (asLink && action.kind === 'open') return <ActionLink href={actionAddress(action)} onClick={event => { event.preventDefault(); press(); }}>{label}</ActionLink>;
  return <>
    {!invitation && !(expanded && action.kind === 'post') && <ActionButton className={lead ? 'opt lead' : 'opt'}
      reading={action.kind === 'open' && !action.open} disabled={busy} onClick={press}>{label}</ActionButton>}
    {expanded && action.kind === 'park' && itemKey && <span className="opts">
      {([['tomorrow', 'Tomorrow morning'], ['monday', 'Monday morning'], ['week', 'In a week']] as const).map(([until, words]) =>
        <ActionButton className="opt" key={until} disabled={busy} onClick={() => void run(async () => {
          await park(store, itemKey, until); onChanged?.();
        })}>{words}</ActionButton>)}
    </span>}
    {/* A post writes, so it asks its reason in the box Sources' pause uses (brief 4.2; the referee, 15 Oct). */}
    {expanded && action.kind === 'post' && <Change drawing label={label} sentence={action.confirm}
      onSave={async reason => {
        if (action.invite) { await sendInvitation(reason); return; }
        gone.current = false;
        try { posted.current = await post(store, action.path, reason); }
        catch (e) { if (e instanceof ApiError && e.status === 404) { gone.current = true; return; } throw e; }
      }}
      onSaved={() => {
        setExpanded(false); onExpanded?.(false);
        if (action.invite) invitationChanged();
        else if (gone.current) onChanged?.(undefined, true);
        else onChanged?.(posted.current?.done ?? action.confirm, false, posted.current);
      }}
      onCancel={() => { setExpanded(false); onExpanded?.(false); }}
      onConflict={() => { setExpanded(false); onExpanded?.(false); onChanged?.(undefined, true); }} />}
    {!expanded && invitation && action.kind === 'post' && action.invite && <InvitationResult result={invitation}
      name={action.invite.name} first={action.invite.first} group={action.invite.group} busy={busy}
      onRetry={() => void run(async () => {
        await sendInvitation(invitationSaved.current!.reason); invitationChanged();
      })}
      onCopy={() => void run(async () => {
        const answer = await addApi.copy(store, action.invite!.principalId);
        await navigator.clipboard.writeText([answer.invite.to, answer.invite.subject, answer.invite.body, answer.invite.link].join('\n\n'));
        setCopied(true);
      })} />}
    {copied && <span className="note" role="status">Copied</span>}
    {error != null && (error instanceof ApiError && error.status === 403
      ? <Refused drawing words={error.message} />
      : <NotSaved drawing reason={error instanceof Error ? error.message : String(error)} onRetry={() => { if (retry.current) void run(retry.current); }} />)}
  </>;
}
